import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAxiosPrivate, {
	toastErrorUnlessHandled,
} from "@/hooks/useAxiosPrivate";
import InviteNbUser from "./InviteNbUser";
import NbUsersTable from "./NbUsersTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import useAppStore from "@/store/AppStore";

export default function NbUsersPage() {
	const [nbUsers, setNbUsers] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const { search } = useLocation();
	const navigate = useNavigate();
	const axiosPrivate = useAxiosPrivate();
	const appStore = useAppStore() as { user: { role: string | null } };
	const canInviteNbUsers = appStore.user?.role === "superAdmin";

	const queryParams = useMemo(() => search, [search]);
	const tab = useMemo(() => {
		const params = new URLSearchParams(queryParams);
		if (!params.has("tab")) return "overview";
		return params.get("tab");
	}, [queryParams]);

	function handleTabChange(tab: string) {
		const params = new URLSearchParams(queryParams);
		params.set("tab", tab);
		navigate(`?${params.toString()}`, { replace: true });
	}

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();
		getNbUsers(isMounted, controller);
		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	async function getNbUsers(
		isMounted = true,
		controller = new AbortController(),
	) {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.get("/auth/nbUsers", {
				signal: controller.signal,
			});

			if (isMounted && response.data?.data) {
				setNbUsers(response.data.data);
			}
		} catch (err) {
			console.error(err);
			toastErrorUnlessHandled(err, "Σφάλμα κατά την ανάκτηση των χρηστών");
		} finally {
			setIsLoading(false);
		}
	}

	async function deleteNbUser(id: number) {
		const isConfirmed = window.confirm(
			"Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον χρήστη;",
		);
		if (!isConfirmed) return;

		try {
			await axiosPrivate.delete(`/auth/nbUsers/${id}`);
			toast.success("Ο χρήστης διαγράφηκε επιτυχώς");
			getNbUsers();
		} catch (error) {
			console.error(error);
			toastErrorUnlessHandled(error, "Σφάλμα κατά τη διαγραφή του χρήστη");
		}
	}

	async function updateUserStatus(id: number, isActive: boolean) {
		try {
			await axiosPrivate.patch(`/auth/nbUsers/${id}`, {
				is_active: isActive,
			});

			toast.success(
				`Ο λογαριασμός χρήστη ${isActive ? "ενεργοποιήθηκε" : "απενεργοποιήθηκε"} επιτυχώς`,
			);
			getNbUsers();
		} catch (error) {
			console.error(error);
			toastErrorUnlessHandled(error, "Σφάλμα κατά την ενημέρωση του χρήστη");
		}
	}

	async function resetUserPassword(id: number): Promise<string | null> {
		try {
			const response = await axiosPrivate.post(
				`/auth/nbUsers/${id}/reset-password`,
			);
			if (response.data?.resetLink) {
				toast.success("Ο κωδικός επαναφέρθηκε επιτυχώς");
				return response.data.resetLink;
			}
			return null;
		} catch (error) {
			console.error(error);
			toastErrorUnlessHandled(
				error,
				"Σφάλμα κατά την επαναφορά του κωδικού",
			);
			throw error;
		}
	}

	// Calculate quick stats
	const activeUsers = nbUsers.filter((user) => user.is_active).length;
	const adminUsers = nbUsers.filter((user) => user.role === "admin").length;
	const regularUsers = nbUsers.length - adminUsers;

	return (
		<div className="flex-1 space-y-4 p-8 pt-6">
			<div className="flex items-center justify-between">
				<h2 className="text-3xl font-bold tracking-tight">
					Διαχείριση Χρηστών Συστήματος
				</h2>
				{canInviteNbUsers && <InviteNbUser getNbUsers={getNbUsers} />}
			</div>

			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Σύνολο Χρηστών
						</CardTitle>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							className="h-4 w-4 text-muted-foreground"
						>
							<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
							<circle cx="9" cy="7" r="4" />
							<path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
						</svg>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{nbUsers.length}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Ενεργοί Χρήστες
						</CardTitle>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							className="h-4 w-4 text-muted-foreground"
						>
							<path d="M22 12h-4l-3 9L9 3l-3 9H2" />
						</svg>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{activeUsers}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Διαχειριστές</CardTitle>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							className="h-4 w-4 text-muted-foreground"
						>
							<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
						</svg>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{adminUsers}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Χρήστες</CardTitle>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							className="h-4 w-4 text-muted-foreground"
						>
							<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
							<circle cx="9" cy="7" r="4" />
						</svg>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{regularUsers}</div>
					</CardContent>
				</Card>
			</div>

			<Tabs
				defaultValue="overview"
				value={tab!}
				onValueChange={(value) => handleTabChange(value)}
				className="space-y-4"
			>
				<TabsList>
					<TabsTrigger value="overview">Σύνοψη</TabsTrigger>
					<TabsTrigger value="details">Αναλυτικά</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="space-y-4 tabs-cont">
					<NbUsersTable
						nbUsers={nbUsers}
						deleteNbUser={deleteNbUser}
						updateUserStatus={updateUserStatus}
						resetUserPassword={resetUserPassword}
						isLoading={isLoading}
					/>
				</TabsContent>

				<TabsContent value="details" className="space-y-4">
					{/* Add detailed user info here if needed */}
				</TabsContent>
			</Tabs>
		</div>
	);
}
